import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { showToast } from 'Core/redux/toast/slice';
import { MoldService } from '../../../service/api/mold-service';
import './webcam-file-upload.css';
import { FaEye } from 'react-icons/fa';
import SegmentWebcamMultiCaptureModal from '../modal/segment-webcam-multi-capture-modal';
import { FaCamera } from 'react-icons/fa';
import '../viewallimagesmodal/view-all-images-modal.css';
import SegmentedImagesModal from '../modal/segmentedimagesmodal/segmented-images-modal';
import { sanitizeUploadKey } from '../sanitizeUploadKeys/sanitizeUploadKeys';

const SegmentWebcamFileUpload = ({ id, onChange, className, documents,isDisabled,toolID,moldInfoID,buttonLabel,maintenanceId = 0, onImageSegmentUploaded, maintenanceData, onMaintenanceSaved, onActivityIssueSaved, showManageImages, context = 'maintenance', onCaptureImageClick, shouldOpenCaptureModal, onCaptureModalOpened, savedMaintenanceActivityData, issueData, categories } ) => {
  console.log('SegmentWebcamFileUpload: Component rendered with props:', {
    id, buttonLabel, isDisabled, documents, showManageImages, context, savedMaintenanceActivityData, issueData
  });
  console.log('SegmentWebcamFileUpload: Context value:', context);
  console.log('SegmentWebcamFileUpload: IssueData value:', issueData);
  const dispatch = useDispatch();
  const [files, setFiles] = useState(documents || []);
  const [showModal, setShowModal] = useState(false);
  
  // Debug modal state changes
  useEffect(() => {
    console.log('SegmentWebcamFileUpload: showModal state changed to:', showModal);
    if (showModal) {
      console.log('SegmentWebcamFileUpload: Modal is opening');
    } else {
      console.log('SegmentWebcamFileUpload: Modal is closing');
    }
  }, [showModal]);

  // Handle shouldOpenCaptureModal prop
  useEffect(() => {
    if (shouldOpenCaptureModal) {
      console.log('SegmentWebcamFileUpload: Opening capture modal from parent');
      setShowModal(true);
      if (onCaptureModalOpened) {
        onCaptureModalOpened();
      }
    }
  }, [shouldOpenCaptureModal, onCaptureModalOpened]);
  const [showViewModal, setShowViewModal] = useState(false); // new modal
  const [selectedImages, setSelectedImages] = useState([]); // Store locally selected images
  
  // Update files when documents prop changes
  useEffect(() => {
    console.log('SegmentWebcamFileUpload: documents prop changed:', documents);
    if (documents && documents.length > 0) {
      setFiles(documents);
      // Also update selectedImages if documents contain local images
      const localImages = documents.filter(doc => doc.isLocal);
      console.log('SegmentWebcamFileUpload: local images found:', localImages);
      if (localImages.length > 0) {
        setSelectedImages(localImages);
      }
    } else {
      // Reset if no documents
      setFiles([]);
      setSelectedImages([]);
    }
  }, [documents]);

  // Debug issueData prop changes
  useEffect(() => {
    console.log('SegmentWebcamFileUpload: issueData prop changed:', issueData);
    if (issueData) {
      console.log('SegmentWebcamFileUpload: issueData.activityIssueId:', issueData.activityIssueId);
      console.log('SegmentWebcamFileUpload: issueData.id:', issueData.id);
      console.log('SegmentWebcamFileUpload: Full issueData object:', JSON.stringify(issueData, null, 2));
    }
  }, [issueData]);

  // Fetch images on mount if we have activityIssueId but no documents (for reopening modal)
  useEffect(() => {
    const fetchImagesOnMount = async () => {
      // Only fetch for other-maintenance-issue context
      if (context !== 'other-maintenance-issue' || !issueData) {
        return;
      }

      const activityIssueId = issueData.activityIssueId || 0;
      
      // Only fetch if we have activityIssueId, maintenanceId, moldInfoID, but no documents
      if (activityIssueId > 0 && maintenanceId > 0 && moldInfoID && (!documents || documents.length === 0) && files.length === 0) {
        console.log('SegmentWebcamFileUpload: Fetching images on mount for activityIssueId:', activityIssueId);
        
        try {
          const response = await MoldService.getMoldMaintenanceSegments(
            moldInfoID,
            'Action Images',
            maintenanceId,
            activityIssueId,
            0
          );

          console.log('SegmentWebcamFileUpload: getMoldMaintenanceSegments response on mount:', response);

          if (response && response.status === 200 && response.response && Array.isArray(response.response) && response.response.length > 0) {
            const apiDocuments = response.response.map(segment => ({
              id: segment.id || segment.segmentId,
              name: segment.title || segment.segmentName || 'Image',
              path: segment.imageUrl || segment.imagePath,
              fileName: segment.fileName || `${segment.segment || 'Image'}_${segment.id || ''}`,
              filePath: segment.imageUrl || segment.imagePath,
              isLocal: false,
              segment: segment.segment || segment.segmentName,
              title: segment.title || '',
              description: segment.description || '',
              actionImageId: activityIssueId,
              subActionImageId: 0
            }));

            console.log('SegmentWebcamFileUpload: Fetched documents on mount:', apiDocuments);
            
            if (apiDocuments.length > 0) {
              setFiles(apiDocuments);
              if (onChange) {
                onChange(apiDocuments);
              }
            }
          }
        } catch (error) {
          console.error('SegmentWebcamFileUpload: Error fetching images on mount:', error);
        }
      }
    };

    // Small delay to ensure component is fully mounted
    const timer = setTimeout(() => {
      fetchImagesOnMount();
    }, 300);

    return () => clearTimeout(timer);
  }, [context, issueData?.activityIssueId, maintenanceId, moldInfoID, documents, files.length]);

  
  return (
    <div className={`file-upload-wrapper ${className}`}>
       <div className="d-flex gap-2 mb-2">
      <button
        type="button"
        className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
        disabled={isDisabled}
        style={{
          opacity: isDisabled ? 0.6 : 1,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
        }}
        onClick={() => {
          if (isDisabled) {
            console.log('SegmentWebcamFileUpload: Capture Image button disabled, click ignored');
            return;
          }
          console.log('SegmentWebcamFileUpload: Capture Image button clicked');
          console.log('SegmentWebcamFileUpload: onCaptureImageClick prop:', onCaptureImageClick);
          console.log('SegmentWebcamFileUpload: Current showModal state:', showModal);
          if (onCaptureImageClick) {
            // Use the custom handler (for confirmation popup)
            console.log('SegmentWebcamFileUpload: Using custom onCaptureImageClick handler');
            onCaptureImageClick();
          } else {
            // Default behavior - open modal directly
            console.log('SegmentWebcamFileUpload: Using default behavior - opening modal');
            setShowModal(true);
          }
        }}
      >
        <FaCamera />
        Capture Image
      </button>
             {/* Manage Images button - always visible when there are images, even in read-only mode */}
             {(showManageImages || files.length > 0 || selectedImages.length > 0) && (
        <button
             type="button"
             className='btn btn-outline-secondary btn-sm d-flex align-items-center gap-2'
             // className="btn-view-all-images"
             onClick={async () => {
               console.log('SegmentWebcamFileUpload: Manage Images button clicked');
               console.log('SegmentWebcamFileUpload: savedMaintenanceActivityData:', savedMaintenanceActivityData);
               console.log('SegmentWebcamFileUpload: savedMaintenanceActivityData type:', typeof savedMaintenanceActivityData);
               console.log('SegmentWebcamFileUpload: savedMaintenanceActivityData keys:', savedMaintenanceActivityData ? Object.keys(savedMaintenanceActivityData) : 'null');
               console.log('SegmentWebcamFileUpload: context:', context);
               console.log('SegmentWebcamFileUpload: buttonLabel:', buttonLabel);
               console.log('SegmentWebcamFileUpload: maintenanceId:', maintenanceId);
               
               // Call getMoldMaintenanceSegments API with actionImageId=0, subActionImageId=0
               if (savedMaintenanceActivityData) {
                 console.log('Calling getMoldMaintenanceSegments with maintenanceId and zero IDs:', {
                   maintenanceId,
                   actionImageId: 0,
                   subActionImageId: 0
                 });
                 
                 try {
                   let recordParam, actionImageId, subActionImageId;
                   
                   // Handle special case for other-maintenance-issue contexts
                   if (context === 'other-maintenance-issue' && issueData) {
                     recordParam = 'Action Images'; // Fixed as per requirement
                     // Use activityIssueId from the issue data (from API response), fallback to 0 for new issues
                     actionImageId = issueData.activityIssueId || 0;
                     subActionImageId = 0; // Set to 0 as per requirement
                     console.log('=== MANAGE IMAGES BUTTON CLICKED ===');
                     console.log('Using other-maintenance-issue parameters:', {
                       recordParam,
                       actionImageId,
                       subActionImageId,
                       issueData: issueData,
                       'issueData.activityIssueId': issueData.activityIssueId,
                       'issueData.id': issueData.id,
                       'Note': 'Using activityIssueId from API response, 0 if new issue'
                     });
                     console.log('This actionImageId will be passed to getMoldMaintenanceSegments API');
                   } else if (context === 'maintenance' && buttonLabel === 'Action Images') {
                     // For Add Actions: use Sub Action Images
                     recordParam = 'Sub Action Images';
                     // Use the passed savedMaintenanceActivityData values instead of hardcoding to 0
                     actionImageId = savedMaintenanceActivityData?.actionImageId || 0;
                     subActionImageId = savedMaintenanceActivityData?.subActionImageId || 0;
                     console.log('Using savedMaintenanceActivityData for maintenance Action Images:', {
                       actionImageId,
                       subActionImageId,
                       savedMaintenanceActivityData
                     });
                   } else {
                     // Regular flow for other contexts
                     recordParam = buttonLabel === 'Action Images' ? 'Action Images' : 'Sub Action Images';
                     // Use the passed savedMaintenanceActivityData values instead of hardcoding to 0
                     actionImageId = savedMaintenanceActivityData?.actionImageId || 0;
                     subActionImageId = savedMaintenanceActivityData?.subActionImageId || 0;
                   }
                   
                   const response = await MoldService.getMoldMaintenanceSegments(
                     moldInfoID,
                     recordParam, // record parameter
                     maintenanceId, // maintenanceId parameter
                     actionImageId, // actionImageId - varies based on context
                     subActionImageId // subActionImageId - set to 0 as per requirement
                   );
                   
                   console.log('getMoldMaintenanceSegments response:', response);
                   console.log('getMoldMaintenanceSegments response structure:', JSON.stringify(response, null, 2));
                   
                   if (response && response.status === 200 && response.response) {
                     console.log('Processing API response, response.response:', response.response);
                     console.log('Response length:', response.response.length);
                     
                     // Update documents with the API response
                     const apiDocuments = response.response.map(segment => ({
                       id: segment.id || segment.segmentId,
                       name: segment.title || segment.segmentName || 'Image',
                       path: segment.imageUrl || segment.imagePath,
                       isLocal: false, // These are from API
                       segment: segment.segment || segment.segmentName,
                       title: segment.title || '',
                       description: segment.description || ''
                     }));
                     
                     console.log('Updated documents from API:', apiDocuments);
                     setFiles(apiDocuments);
                     onChange(apiDocuments);
                   } else {
                     console.log('No valid response or empty response:', response);
                   }
                 } catch (error) {
                   console.error('Error fetching segments:', error);
                   dispatch(showToast({ 
                     message: 'Failed to load segments', 
                     type: 'danger' 
                   }));
                 }
               } else {
                 console.log('SegmentWebcamFileUpload: No savedMaintenanceActivityData available, opening modal without API call');
                 console.log('SegmentWebcamFileUpload: savedMaintenanceActivityData details:', {
                   exists: !!savedMaintenanceActivityData,
                   actionImageId: savedMaintenanceActivityData?.actionImageId,
                   subActionImageId: savedMaintenanceActivityData?.subActionImageId
                 });
               }
               
               setShowViewModal(true);
             }}
           >
             <FaEye />
             Manage Images
           </button>
           )}
        </div>
      {showModal && (() => {
        console.log('SegmentWebcamFileUpload: Rendering SegmentWebcamMultiCaptureModal with showModal:', showModal);
        return (
             <SegmentWebcamMultiCaptureModal
         show={showModal}
         toolID={toolID}
         moldInfoID={moldInfoID}
         buttonLabel={buttonLabel}
         maintenanceId={maintenanceId}
        maintenanceData={maintenanceData}
                   onClose={() => setShowModal(false)}
          onImageSegmentUploaded={onImageSegmentUploaded}
        onMaintenanceSaved={onMaintenanceSaved}
        onActivityIssueSaved={onActivityIssueSaved}
          savedMaintenanceActivityData={savedMaintenanceActivityData}
          issueData={issueData}
          context={context}
          categories={categories}
        onSubmit={uploadedFiles => {
          console.log('SegmentWebcamFileUpload: onSubmit called with:', uploadedFiles);
          if (uploadedFiles.length > 0) {
            //const first = uploadedFiles[0];

            const newDocuments = uploadedFiles?.map(file => ({
              id: id, // or generate a unique ID per file if needed
              name: file.fileName,
              path: file.filePath,
              isLocal: file.isLocal || false, // Track if it's a local file
            }));

            console.log('SegmentWebcamFileUpload: processed newDocuments:', newDocuments);
            console.log('SegmentWebcamFileUpload: calling onChange with:', newDocuments);

            // Store selected images locally
            setSelectedImages(prev => {
              const updated = [...prev, ...uploadedFiles];
              console.log('SegmentWebcamFileUpload: updated selectedImages:', updated);
              return updated;
            });

            onChange(newDocuments);

           // setFiles([ ...files, ...newDocuments ]);
           setFiles(newDocuments);
          }
        }}
      />
        );
      })()}


 {/* <ViewAllImagesModal
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
        documents={files}
      /> */}
      {/* <SegmentedImagesModal
   show={showViewModal}
   onClose={() => setShowViewModal(false)}
   documents={files}
 /> */}

 {/* Debug: Log what props are being passed to SegmentedImagesModal */}


 {showViewModal && (() => {
      const documentsToPass = selectedImages.length > 0 ? selectedImages : (documents || files);
      console.log('SegmentWebcamFileUpload: Passing to SegmentedImagesModal:', {
        selectedImages,
        documents,
        files,
        documentsToPass,
        buttonLabel,
        showLocalImages: selectedImages.length > 0 || buttonLabel === "Capture Image",
        savedMaintenanceActivityData
      });
      return (
   <SegmentedImagesModal
     show={showViewModal}
     onClose={() => setShowViewModal(false)}
          documents={documentsToPass}
     moldInfoID={moldInfoID}
     buttonLabel={context === 'other-maintenance-issue' ? 'Action Images' : buttonLabel}
     maintenanceId={maintenanceId}
     context={context}
          showLocalImages={selectedImages.length > 0 || buttonLabel === "Capture Image"} // Flag to indicate we should show local images
          savedMaintenanceActivityData={savedMaintenanceActivityData}
          issueData={issueData}
     onDelete={async (slotName, index, imageToDelete) => {
       try {
         // Use the image object passed from SegmentedImagesModal if available, otherwise fall back to files[index]
         const fileToDelete = imageToDelete || files[index];
         if (fileToDelete && fileToDelete.id) {
           // Call the API to delete the image segment
           const response = await MoldService.deleteMoldMaintenanceImageSegment(fileToDelete.id);
           
           if (response.status === 200) {
             
             // Remove from local state immediately
             const updated = files.filter((_, i) => i !== index);
             setFiles(updated);
             onChange(updated);
             
             // Show success toast
             dispatch(showToast({ 
               message: 'Image deleted successfully', 
               type: 'success' 
             }));
           } else {
             dispatch(showToast({ 
               message: 'Failed to delete image', 
               type: 'danger' 
             }));
           }
         } else {
           // Still remove from local state even if no ID
           const updated = files.filter((_, i) => i !== index);
           setFiles(updated);
           onChange(updated);
         }
       } catch (error) {
         dispatch(showToast({ 
           message: 'Error deleting image: ' + (error.message || 'Unknown error'), 
           type: 'danger' 
         }));
       }
     }}
   />
      );
    })()}

{/* inline file list rendering here */}
      {/* {files?.map(file => (
        <div className="file-display-box mt-2">
          <span className="file-name-text" title={file.name}>
            {file.name}
          </span>
          <a href={file.path} target="_blank" rel="noopener noreferrer" className="eye-icon-right">
            <FaEye />
          </a>
        </div>
      ))} */}

      {/* {fileName && fileUrl && (
        <div className="file-display-box mt-2">
          <span className="file-name-text" title={fileName}>
            {fileName}
          </span>
          <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="eye-icon-right">
            <FaEye />
          </a>
        </div>
      )} */}
    </div>
  );
};

  SegmentWebcamFileUpload.propTypes = {
    id: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    className: PropTypes.string,
    value: PropTypes.string,
    filePath: PropTypes.string,
    isDisabled: PropTypes.bool,
    toolID: PropTypes.string,
    moldInfoID: PropTypes.number,
    buttonLabel: PropTypes.string,
    maintenanceId: PropTypes.number,
    onImageSegmentUploaded: PropTypes.func,
    maintenanceData: PropTypes.object,
    onMaintenanceSaved: PropTypes.func,
    showManageImages: PropTypes.bool,
    context: PropTypes.string,
  };

export default SegmentWebcamFileUpload;
